/**
 * Usage tab — cross-study consumers of a concept group.
 *
 * Surfaces the "shared across studies" relationship in the platform's
 * resource hierarchy: this concept group is anchored by N analytical variables in M
 * studies. Lets the epi see who depends on this group before changing
 * it.
 *
 * Grouped by study so the impact of an edit is scannable. analytical variables and
 * cohorts are differentiated by chip color.
 */

import { Box, Chip, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';

import type { EpiArtifact, EpiConceptGroupUsage } from '../../types/epi-chat-types';

const groupByStudy = (
  usage: EpiConceptGroupUsage[]
): Array<{ studyId: string | null; studyLabel: string; entries: EpiConceptGroupUsage[] }> => {
  const map = new Map<string, { studyLabel: string; entries: EpiConceptGroupUsage[] }>();
  for (const entry of usage) {
    const key = entry.studyId ?? '__no_study__';
    const label = entry.studyLabel ?? 'Unscoped';
    const slot = map.get(key);
    if (slot) {
      slot.entries.push(entry);
    } else {
      map.set(key, { studyLabel: label, entries: [entry] });
    }
  }
  return Array.from(map.entries()).map(([key, value]) => ({
    studyId: key === '__no_study__' ? null : key,
    studyLabel: value.studyLabel,
    entries: value.entries,
  }));
};

export const UsageTab = ({ artifact }: { artifact: EpiArtifact }) => {
  // Memoize the fallback so the `useMemo` below has a stable input even
  // when `artifact.usage` is undefined.
  const usage = useMemo(() => artifact.usage ?? [], [artifact.usage]);
  const grouped = useMemo(() => groupByStudy(usage), [usage]);

  if (usage.length === 0) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No consumers tracked yet. Once an analytical variable or cohort references this concept
          group its usage will appear here, grouped by study.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ padding: 1.5 }} data-testid="artifact-usage-tab">
      <Typography variant="caption" color="text.secondary">
        {`Used by ${usage.length} consumer${usage.length === 1 ? '' : 's'} across ${
          grouped.length
        } ${grouped.length === 1 ? 'study' : 'studies'}`}
      </Typography>
      {grouped.map((study) => (
        <Box
          key={study.studyId ?? study.studyLabel}
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              paddingX: 1.5,
              paddingY: 0.75,
              backgroundColor: (theme) => theme.palette.action.hover,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle2">{study.studyLabel}</Typography>
          </Box>
          <Stack divider={<Box sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }} />}>
            {study.entries.map((entry) => (
              <Stack
                key={`${entry.consumerKind}:${entry.consumerId}`}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ paddingX: 1.5, paddingY: 0.75 }}
              >
                <Chip
                  size="small"
                  label={entry.consumerKind === 'av' ? 'Variable' : 'Cohort'}
                  color={entry.consumerKind === 'av' ? 'primary' : 'secondary'}
                  variant="outlined"
                  sx={{ height: 20, fontSize: (t) => t.typography.caption.fontSize }}
                />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {entry.consumerLabel}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace" noWrap>
                  {entry.consumerId}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
